import { useId } from 'react';
import { styled } from '@/styles/stitches.config';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';
import is from '@sindresorhus/is';
import { packSiblings } from 'd3-hierarchy';
import { AnimatePresence, motion } from 'framer-motion';
import { ChartOverlay, ChartRoot, ChartSvg, Point } from './chart';
import { Label } from './label';

type DotMatrixProps<T> = ComposableStyledComponentProps<HTMLDivElement> & {
  label: string;
  // data
  data?: T;
  // extracts from data the value to show dots for on the left
  getLeft: (d: T) => number;
  // extracts from data the value to show dots for on the right
  getRight: (d: T) => number;
  // should transform the result of getLeft or getRight into an integer
  getNumberOfCircles: (d: number) => number;
  // returns the label for a data point
  getLabel: (side: 'left' | 'right') => string;
  // format the value into a string
  formatValue: (d: number) => string;
};

const CIRCLE_RADIUS = 1.8;
const CIRCLE_PADDING = 0.45;

type Circle = {
  r: number;
  name: string;
};

const Value = styled('span', {
  fontWeight: '$semibold',
  fontSize: '$3',
  color: 'var(--color-text-light)',
});

export function DotMatrix<T>(props: DotMatrixProps<T>) {
  const {
    css,
    data,
    label,
    getNumberOfCircles = (d: number) => d,
    getLeft,
    getRight,
    getLabel,
    formatValue = (d: number) => String(d),
  } = props;

  const leftId = useId();
  const rightId = useId();

  if (!data) return null;

  let left, right;

  if (is.number(getLeft(data))) {
    left = packSiblings<Circle>(
      new Array(getNumberOfCircles(getLeft(data)))
        .fill({ r: CIRCLE_RADIUS + CIRCLE_PADDING, name: '' })
        .map((el, idx) => ({ ...el, name: `left-${idx + 1}%` }))
    );
  }

  if (is.number(getRight(data))) {
    right = packSiblings<Circle>(
      new Array(getNumberOfCircles(getRight(data)))
        .fill({ r: CIRCLE_RADIUS + CIRCLE_PADDING, name: '' })
        .map((el, idx) => ({ ...el, name: `right-${idx + 1}%` }))
    );
  }

  return (
    <ChartRoot
      css={{
        alignSelf: 'center',
        ...css,
      }}
    >
      <ChartSvg viewBox="0 0 100 100" role="figure">
        {left && (
          <g transform="translate(25, 50)">
            <AnimatePresence>
              {left.map((el, i) => (
                <motion.circle
                  initial={{ opacity: 0, cx: 0, cy: 0 }}
                  animate={{ cx: el.x, cy: el.y, opacity: 1, transition: { delay: i * 0.015 } }}
                  exit={{ opacity: 0 }}
                  key={el.name}
                  r={CIRCLE_RADIUS}
                  fill="var(--color-primary)"
                />
              ))}
            </AnimatePresence>
          </g>
        )}

        {right && (
          <g transform="translate(75, 50)">
            <AnimatePresence>
              {right.map((el, i) => (
                <motion.circle
                  initial={{ opacity: 0, cx: 0, cy: 0 }}
                  animate={{ cx: el.x, cy: el.y, opacity: 1, transition: { delay: i * 0.015 } }}
                  exit={{ opacity: 0 }}
                  key={el.name}
                  r={CIRCLE_RADIUS}
                  fill="var(--color-secondary)"
                />
              ))}
            </AnimatePresence>
          </g>
        )}
      </ChartSvg>

      <ChartOverlay>
        {label && (
          <Point
            css={{
              '--top': '10%',
              '--left': '50%',
              color: 'var(--color-text-light)',
              textAlign: 'center',
              width: '75%',
            }}
          >
            <Label>{label}</Label>
          </Point>
        )}

        {is.number(getLeft(data)) && (
          <Point
            aria-labelledby={leftId}
            css={{
              '--top': '50%',
              '--left': '25%',
              background: '$blue0',
              borderRadius: '1.5em',
              paddingInline: '$2',
            }}
          >
            <Value>{formatValue(getLeft(data))}</Value>
          </Point>
        )}

        {is.number(getRight(data)) && (
          <Point
            aria-labelledby={rightId}
            css={{
              '--top': '50%',
              '--left': '75%',
              background: '$blue0',
              borderRadius: '1.5em',
              paddingInline: '$2',
              whiteSpace: 'nowrap',
            }}
          >
            <Value>{formatValue(getRight(data))}</Value>
          </Point>
        )}

        {getLabel('left') && (
          <Point
            id={leftId}
            css={{
              '--top': '90%',
              '--left': '25%',
              color: 'var(--color-text-light)',
              textAlign: 'center',
            }}
          >
            <Label css={{ width: '10rem' }}>{getLabel('left')}</Label>
          </Point>
        )}

        {getLabel('right') && (
          <Point
            id={rightId}
            css={{
              '--top': '90%',
              '--left': '75%',
              color: 'var(--color-text-light)',
              textAlign: 'center',
            }}
          >
            <Label css={{ width: '10rem' }}>{getLabel('right')}</Label>
          </Point>
        )}
      </ChartOverlay>
    </ChartRoot>
  );
}
